// eslint-disable-next-line import/no-cycle,restrict-imports/restrict-imports
import { getScrollingManager } from '../../mobile-library/contentFrameInternals/getScrollingManager';
import { HorizontalPaginatedScrollingManager } from '../../mobile-library/contentFrameInternals/HorizontalPaginatedScrollingManager';
import { ScrollingManager } from '../../mobile-library/contentFrameInternals/ScrollingManager';
import type { TtsPosition, WordBoundary } from '../../types';
import { LenientWindow } from '../../types/LenientWindow';
import { isInReactNativeWebView } from '../../utils/environment';
import { getChunkContainerByChunkId, getCurrentChunkIndex } from '../chunkElement';
// eslint-disable-next-line import/no-cycle
import { portalGate as portalGateToForeground } from '../portalGates/contentFrame/from/reactNativeWebview';
import { getChunkScrollDepthInWeb } from '../scrollDepth';
import { getFocusableElementFromSerializedPosition } from '../serializedPosition';
import { ChunkContainerElement } from '../types/chunkedDocuments';
import { ChunkScrollPosition } from '../types/events';
import convertRangeToRangyRange from '../utils/convertRangeToRangyRange';
import { findCenteredElementInViewport } from '../utils/findCenteredElementInViewport';
import getRangyClassApplier from '../utils/getRangyClassApplier';
import getVisibilityDetails from '../utils/getVisibilityDetails';
import { serializeRangeAsCanonical } from '../utils/locationSerialization/chunked';
import { TTS_CHARS_PER_SECOND } from '../utils/tts';
import { forceChunkContentLoadAtPosition, getChunkContainerElements } from './chunkedContent';
import { findWordBoundaryForTrackPosition } from './findWordBoundaryForTrackPosition';
import {
  findTTSableElementFromCenterOfViewport,
  findTtsAbleNode,
  populateTtsAbleElements,
  TextToSpeechContentFrameError,
} from './textToSpeechUtils';
import scrollIntoView from './utils/scrollIntoView';

let lastTtsTextPos: number | null = null;
let lastTtsRectBounds: {
  clientRects: DOMRectList; // to figure out if our word boundary actually spans two lines, if like a word is wrapping
  containerRect: DOMRect; // what we use instead of rect (which could be called wordRect) if perfect word match is false
  perfectWordMatch: boolean; // if we match on word or surrounding container
  rect: DOMRect; // which we can scroll to
  range: Range; // the generated range itself
};
let ttsAbleElements: HTMLElement[] = [];
declare let window: LenientWindow;

let wordBoundariesForVoice: {[chunkId: string]: WordBoundary[];} | null = null;

function getIndicatorElements() {
  const start = document.querySelector<HTMLElement>('.tts-position-indicator-start');
  const end = document.querySelector<HTMLElement>('.tts-position-indicator-end');
  if (!start || !end) {
    throw new Error('TTS position indicator elements not found');
  }
  return {
    start,
    end,
  };
}

export async function getFocusableElementIndexFromCurrentScrollPosition({
  contentContainer,
  ttsAbleElements,
  window,
}: {
  contentContainer: HTMLElement;
  ttsAbleElements: HTMLElement[];
  window: LenientWindow;
}): Promise<
  | {
      elementIndex: number;
      position: number;
    }
  | undefined
> {
  const { element, index } = findTTSableElementFromCenterOfViewport(
    contentContainer,
    ttsAbleElements,
    window,
  );
  if (!element) {
    throw new TextToSpeechContentFrameError('cannot play TTS, no TTSable element found from selection');
  }
  let precedingElementsTextLength = 0;
  // Add text length up to the parent node
  for (const ttsAbleElement of ttsAbleElements) {
    if (element === ttsAbleElement) {
      break;
    }
    precedingElementsTextLength += ttsAbleElement.textContent?.length ?? 0;
  }
  // We need to recursively add up all text up to this selection within the node
  const startOffset = precedingElementsTextLength;
  const position = startOffset / TTS_CHARS_PER_SECOND;
  return {
    elementIndex: index,
    position,
  };
}

export function populateOrRepopulateTTSAbleElements(contentContainer: HTMLElement) {
  if (ttsAbleElements.length > 0) {
    const firstElement = ttsAbleElements[0];
    if (!contentContainer.contains(firstElement)) {
      ttsAbleElements = [];
    }
  }
  if (!ttsAbleElements.length) {
    populateTtsAbleElements(contentContainer, ttsAbleElements);
  }

  window.tts.ttsAbleElements = ttsAbleElements;
}

export async function getPositionForTtsFromCurrentScrollPosition({
  getIsDocumentScrolledToTop,
  window,
}: {
  getIsDocumentScrolledToTop(): boolean;
  window: LenientWindow;
}): Promise<
  | {
      elementIndex?: number;
      position: number;
      chunkId: string;
    }
  | undefined
> {

  let contentContainer: ChunkContainerElement | null = null;
  if (isInReactNativeWebView) {
    contentContainer = getScrollingManager().getCurrentChunkContainer();
  } else {

    contentContainer = findCenteredElementInViewport(Array.from(getChunkContainerElements()), window);
  }

  if (!contentContainer) {
    throw new TextToSpeechContentFrameError('No contentContainer');
  }
  const chunkId = contentContainer.dataset.chunkId;
  if (getIsDocumentScrolledToTop()) {
    return { position: 0, chunkId };
  }
  populateOrRepopulateTTSAbleElements(contentContainer);
  if (!wordBoundariesForVoice) {
    // If we have no word boundaries, guess the timestamp
    const result = await getFocusableElementIndexFromCurrentScrollPosition({
      contentContainer,
      ttsAbleElements,
      window,
    });
    if (!result) {
      return undefined;
    }
    return { ...result, chunkId };
  }
  const { element } = findTTSableElementFromCenterOfViewport(contentContainer, ttsAbleElements, window);

  if (!element) {
    throw new TextToSpeechContentFrameError(
      'getPositionForTtsFromCurrentScrollPosition: no containingEl',
    );
  }
  // Once we have a containing node, find it inside our list of TTSable nodes
  const nodeIndex = ttsAbleElements.indexOf(element);
  // Get the offset of the selection
  const wordBoundariesForVoiceAndChunk = wordBoundariesForVoice?.[chunkId] ?? [];
  const closestWordBoundary = wordBoundariesForVoiceAndChunk.find((wordBoundary) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_trackPos, _textPos, _word, _paraTextPos, paraIndex] = wordBoundary;
    if (paraIndex < nodeIndex) {
      return false;
    }
    return paraIndex === nodeIndex;
  });
  // This offset might match an existing word boundary
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // this.ttsAutoScrollingEnabled = true;
  // If we failed to match the word, revert to guessing
  if (!closestWordBoundary || closestWordBoundary[4] !== nodeIndex) {
    const result = await getFocusableElementIndexFromCurrentScrollPosition({
      contentContainer,
      ttsAbleElements,
      window,
    });
    window.getSelection()?.removeAllRanges();
    if (!result) {
      return undefined;
    }
    return { ...result, chunkId };
  }
  window.getSelection()?.removeAllRanges();
  // But we probably didnt fail, so use the word timestamp!
  return { position: closestWordBoundary[0], chunkId };
}

export async function getPositionForTtsFromCurrentScrollPositionUsingIds(
  scrollableRootId: string,
): ReturnType<typeof getPositionForTtsFromCurrentScrollPosition> {
  const scrollableRoot = document.getElementById(scrollableRootId);
  if (!scrollableRoot) {
    throw new Error('scrollableRoot not found');
  }
  return getPositionForTtsFromCurrentScrollPosition({
    getIsDocumentScrolledToTop: () => scrollableRoot.scrollTop === 0,
    window,
  });
}

export function getRectsFromTtsPosition({
  contentContainer,
  scrollableRoot,
  ttsPosition,
  returnRepeatedValue = false,
}: {
  contentContainer: HTMLElement | undefined;
  scrollableRoot: Element;
  ttsPosition: TtsPosition;
  returnRepeatedValue?: boolean;
}):
  | {
      clientRects: DOMRectList; // to figure out if our word boundary actually spans two lines, if like a word is wrapping
      containerRect: DOMRect; // what we use instead of rect (which could be called wordRect) if perfect word match is false
      perfectWordMatch: boolean; // if we match on word or surrounding container
      rect: DOMRect; // which we can scroll to
      range: Range; // the generated range itself
    }
  | undefined {
  if (!contentContainer) {
    throw new TextToSpeechContentFrameError('Content container not found');
  }

  const { textPos, word: ttsWord, paraTextPos, paraIndex, wordLength: ttsWordLength } = ttsPosition;
  // strip new line chars from the word
  const word = ttsWord.replace(/\r?\n|\r/g, '');
  // For DRM protected documents, word is empty and wordLength is provided separately
  const wordLength = word ? word.length : ttsWordLength ?? 0;
  // Use textPos for deduplication - more reliable than word since same word can appear multiple times
  if (textPos === lastTtsTextPos) {
    return returnRepeatedValue ? lastTtsRectBounds : undefined;
  }
  lastTtsTextPos = textPos;
  if (wordLength === 1 && word.match(/[!#$%&()*,./:;=?\\^_`{}~-]/g)) {
    // Skip punctuation words
    return;
  }

  populateOrRepopulateTTSAbleElements(contentContainer);

  let wordOffset = 0;
  let currentNode;
  if (ttsAbleElements[paraIndex]) {
    currentNode = ttsAbleElements[paraIndex];
    wordOffset = paraTextPos;
  }
  if (!currentNode) {
    // Fallback to searching through entire document for the node using text position
    [currentNode, wordOffset] = findTtsAbleNode(textPos, ttsAbleElements);
  }

  if (!currentNode || !currentNode.textContent) {
    return;
  }

  // We need to find the exact textNode inside the currentNode

  // New traverse idea
  // Loop through children, adding up their text content until we surpass the wordOffset
  // Once we do that, the previous child will be the right node
  // Get the paragraph length up to that point, and recurse until the right node is a text node
  if (wordOffset >= currentNode.textContent.length) {
    wordOffset = currentNode.textContent.length - 1;
  }
  // If the current node is a LI item inside an OL, add some padding for the number
  if (currentNode.nodeName === 'LI' && currentNode.parentNode?.nodeName === 'OL') {
    wordOffset -= 3;
    if (wordOffset < 0) {
      wordOffset = 0;
    }
  }

  const traverse = (currentNode: Node, startingOffset: number): Node | undefined => {
    if (currentNode.nodeType === 3) {
      // The found text node will be offset from the parent html element by startingOffset amount. This needs to be reflected
      // in the wordOffset we found previously, which was relative to the parent HTML element
      wordOffset -= startingOffset;
      return currentNode;
    }
    // Loop through children, adding up their text content until we surpass the wordOffset
    let textOffset = startingOffset;
    if (currentNode.childNodes.length === 1) {
      return traverse(currentNode.childNodes[0], textOffset);
    }
    for (const child of currentNode.childNodes) {
      const childTextContent = child.textContent;
      if (!childTextContent) {
        continue;
      }
      if (textOffset + childTextContent.length > wordOffset) {
        // we have surpassed the wordOffset, the word should be in this child
        return traverse(child, textOffset);
      }
      textOffset += childTextContent.length;
    }
  };

  // We now should have the exact text content node that contains our word
  const lastSeenTextNode = traverse(currentNode, 0);


  if (!lastSeenTextNode || !lastSeenTextNode.textContent) {
    throw new TextToSpeechContentFrameError('Could not find text node');
  }
  // Using the text node, find the exact range containing the word
  // If we cannot find that, fallback to selecting the entire element
  let perfectWordMatch = true;
  const range = new Range();
  range.setStart(lastSeenTextNode, 0);
  range.collapse(true);
  range.setStart(lastSeenTextNode, wordOffset);
  range.setEnd(
    lastSeenTextNode,
    Math.min(wordOffset + wordLength, lastSeenTextNode.textContent.length),
  );

  const clientRects = range.getClientRects();
  // If the word does not match, fallback to the entire row
  // For DRM protected documents, word is empty so skip word validation
  const wordValidationFailed = word && !range.toString().toLowerCase().startsWith(word.toLowerCase());
  if (range.collapsed || wordValidationFailed || clientRects.length > 2) {
    // If the range starts with the word, lets just highlight that part
    range.setStart(lastSeenTextNode, Math.max(0, wordOffset - 50));
    range.setEnd(
      lastSeenTextNode,
      Math.min(lastSeenTextNode.textContent.length, wordOffset + wordLength + 50),
    );
    perfectWordMatch = false;
  }

  const rect = perfectWordMatch ? range.getClientRects()[0] : range.getBoundingClientRect();
  const containerRect = lastSeenTextNode.parentElement
    ? lastSeenTextNode.parentElement.getBoundingClientRect()
    : scrollableRoot.getBoundingClientRect();
  if (!rect?.width) {
    return;
  }
  const result = {
    clientRects,
    containerRect,
    perfectWordMatch,
    range,
    rect,
  };
  lastTtsRectBounds = result;
  return result;
}

export async function hideWordBoundaryIndicator() {
  const indicatorElements = getIndicatorElements();

  indicatorElements.start.style.top = `${0}px`;
  indicatorElements.start.style.left = `${0}px`;
  indicatorElements.start.style.height = `${0}px`;
  indicatorElements.start.style.width = `${0}px`;
  indicatorElements.start.style.opacity = '0';
  indicatorElements.start.style.transform = `translateX(${0}px)`;

  indicatorElements.end.style.top = `${0}px`;
  indicatorElements.end.style.left = `${0}px`;
  indicatorElements.end.style.height = `${0}px`;
  indicatorElements.end.style.width = `${0}px`;
  indicatorElements.end.style.opacity = '0';
  indicatorElements.end.style.transform = `translateX(${0}px)`;

  setTimeout(() => {
    indicatorElements.start.classList.remove('tts-position-indicator--with-transition');
    indicatorElements.end.classList.remove('tts-position-indicator--with-transition');
  }, 0);
}

export async function onDocumentIdChangedForTts() {
  lastTtsTextPos = null;
  ttsAbleElements = [];
  wordBoundariesForVoice = null;
  window.tts.ttsAbleElements = ttsAbleElements;
  window.tts.wordBoundariesForVoice = wordBoundariesForVoice;
}

export async function scrollToTtsPosition({
  contentContainer,
  isAutoScrollEnabled,
  scrollableRoot,
  ttsPosition,
  ttsChunkId,
  isTtsActivelyPlaying = true,
  skipIndicatorUpdate = false,
  useRepeatedLastWord = false,
}: {
  contentContainer?: ChunkContainerElement;
  isAutoScrollEnabled: boolean;
  scrollableRoot: HTMLElement;
  ttsPosition: TtsPosition;
  ttsChunkId?: string;
  isTtsActivelyPlaying?: boolean;
  skipIndicatorUpdate?: boolean;
  useRepeatedLastWord?: boolean;
}) {
  const mobileScrollingManager = isInReactNativeWebView ? getScrollingManager('scrollToTTSPosition') : undefined;

  if (mobileScrollingManager && ttsChunkId && ttsChunkId !== mobileScrollingManager.getCurrentChunkId()) {
    // TTS is speaking in another chunk: a rect lookup against this chunk is meaningless
    // and would poison the textPos dedupe cache in getRectsFromTtsPosition, starving the
    // first real update after the user swipes to the spoken chunk. Clear the re-lock
    // target and bail before computing rects.
    mobileScrollingManager.noteCurrentTTSLocation(null, ttsChunkId);
    return;
  }

  // The interval fires faster than words change — those repeated-position ticks return
  // no rect by design (textPos dedupe) and must not disturb the current note.
  const isRepeatedTtsPosition = ttsPosition.textPos === lastTtsTextPos;

  const rectsResult = getRectsFromTtsPosition({
    contentContainer,
    scrollableRoot,
    ttsPosition,
    returnRepeatedValue: useRepeatedLastWord,
  });

  // Even with auto-scroll off, the paginated managers track which page TTS is speaking
  // on so that returning to it re-locks auto-scroll. Runs before the rect bail-out so a
  // failed lookup for a new position clears the note instead of leaving it stale. Paused
  // playback keeps calling this on an interval — only note while actively playing;
  // otherwise drop the note so a paused location is never re-lockable.
  if (isTtsActivelyPlaying) {
    if (rectsResult || !isRepeatedTtsPosition) {
      mobileScrollingManager?.noteCurrentTTSLocation(rectsResult?.rect ?? null, ttsChunkId);
    }
  } else {
    mobileScrollingManager?.clearCurrentTTSLocationNote();
  }

  if (!rectsResult) {
    return;
  }

  if (!skipIndicatorUpdate) {
    await updateTtsIndicator({
      ...rectsResult,
      contentContainer,
      scrollingManager: mobileScrollingManager,
      scrollableRoot,
    });
  }

  if (isAutoScrollEnabled) {
    if (mobileScrollingManager) {
      mobileScrollingManager.scrollViewportToCurrentTTSLocation(rectsResult.rect);
    } else {
      // Is it in the top 70% of the scrollable root / viewport?
      const { isTopInView } = await getVisibilityDetails({
        canCauseReflow: true,
        rootPercentRange: {
          top: 0,
          bottom: 0.7,
        },
        scrollableRoot,
        subject: rectsResult.rect,
      });

      if (!isTopInView) {
        await scrollIntoView(rectsResult.rect, {
          behavior: 'smooth',
          block: 'start',
          margin: 128, // At least `--sidebar-nav-height` x2 so we don't show an indicator on top of the page header
          scrollableAncestor: scrollableRoot,
        });
      }
    }
  }
}

export async function setTtsWordBoundariesForVoice(value: {[chunkId: string]: WordBoundary[];} | null = null) {
  wordBoundariesForVoice = value;
  window.tts.wordBoundariesForVoice = wordBoundariesForVoice;
}

async function getReadingProgressForWeb(
  serializedPosition: string,
  contentContainer: HTMLElement,
): Promise<ChunkScrollPosition> {
  const chunkId = await forceChunkContentLoadAtPosition(serializedPosition);
  if (!chunkId) {
    return { chunkIndex: 0, scrollDepthWithinChunk: 0 };
  }
  const chunkElement = getChunkContainerByChunkId(chunkId);
  const serializedPositionElement = getFocusableElementFromSerializedPosition(serializedPosition, contentContainer);
  if (!serializedPositionElement || !chunkElement) {
    return { chunkIndex: 0, scrollDepthWithinChunk: 0 };
  }
  return {
    chunkIndex: Number(chunkElement?.dataset.chunkIndex),
    scrollDepthWithinChunk: getChunkScrollDepthInWeb(contentContainer, serializedPositionElement),
  };
}

async function sendTTSIndicatorMovedEvent(
  scrollingManager: ScrollingManager | undefined,
  contentContainer: ChunkContainerElement,
  range: Range,
  webScrollableRoot: HTMLElement,
): Promise<void> {
  const scrollableRoot = scrollingManager
        ? scrollingManager.getScrollingElement()
        : webScrollableRoot;

  if (!contentContainer.parentElement) {
    return;
  }
  const serializedLocation = serializeRangeAsCanonical({
    classApplier: getRangyClassApplier(),
    range: convertRangeToRangyRange(range),
    containerNode: contentContainer.parentElement,
  });

  // use the ending position from location
  const serializedPosition = serializedLocation.split(',')[1];

  const chunkScrollDepth = scrollingManager
    ? scrollingManager.getScrollDepthWithinCurrentChunk(range)
    : getChunkScrollDepthInWeb(contentContainer);

  const readingPositionData: ChunkScrollPosition | undefined = scrollingManager
    ? await scrollingManager.getReadingPositionData()
    : await getReadingProgressForWeb(serializedPosition, contentContainer);

  const chunkIndex = scrollingManager
    ? scrollingManager.currentChunkIndex
    : getCurrentChunkIndex(contentContainer);

  if (chunkIndex === null) {
    return;
  }

  const serializedElementVerticalOffset = (scrollableRoot?.clientHeight ?? 0) / 2;

  portalGateToForeground.emit('tts-indicator-moved', {
    scrollDelta: 0,
    currentScrollPositionData: {
      chunkIndex,
      scrollDepthWithinChunk: chunkScrollDepth,
      serializedElementVerticalOffset,
      serializedPosition,
    },
    currentReadingPositionData: readingPositionData,
  });
}

/*
  Given some new rects from a text selection
  Update the TTS slugs to wrap the text
*/
export async function updateTtsIndicator({
  clientRects,
  containerRect,
  contentContainer,
  scrollableRoot,
  perfectWordMatch,
  range,
  rect,
  scrollingManager,
}: {
  clientRects: DOMRectList;
  containerRect: DOMRect;
  contentContainer?: ChunkContainerElement;
  scrollableRoot: HTMLElement;
  perfectWordMatch: boolean;
  range: Range;
  rect: DOMRect;
  scrollingManager: ScrollingManager | undefined;
}): Promise<number> {
  const ttsIndicatorElements = getIndicatorElements();
  const isHorizontalPagination = scrollingManager instanceof HorizontalPaginatedScrollingManager;
  let scrollableRootTop = scrollingManager
        ? scrollingManager.getScrollingElementTop()
        : scrollableRoot.scrollTop ?? 0;

  if (isHorizontalPagination) {
    scrollableRootTop = 0;
  }

  const ttsPosTop = Math.floor(scrollableRootTop + rect.top);
  const ttsWidth = perfectWordMatch ? rect.width : containerRect.width;
  const ttsTransformLeft = perfectWordMatch ? rect.left : containerRect.left;
  const ttsHeight = rect.height;

  ttsIndicatorElements.start.style.top = `${ttsPosTop}px`;
  ttsIndicatorElements.start.style.height = `${ttsHeight}px`;
  ttsIndicatorElements.start.style.width = `${ttsWidth}px`;
  ttsIndicatorElements.start.style.opacity = '1';
  ttsIndicatorElements.start.style.transform = `translateX(${ttsTransformLeft}px)`;
  ttsIndicatorElements.start.classList.remove('tts-position-indicator-large');

  if (!perfectWordMatch && clientRects.length !== 2) {
    ttsIndicatorElements.start.classList.add('tts-position-indicator-large');
  }

  if (!perfectWordMatch || clientRects.length !== 2) {
    ttsIndicatorElements.end.style.top = `${ttsPosTop}px`;
    ttsIndicatorElements.end.style.height = `${0}px`;
    ttsIndicatorElements.end.style.width = `${0}px`;
    ttsIndicatorElements.end.style.opacity = '0';
    ttsIndicatorElements.end.style.transform = `translateX(${ttsTransformLeft}px)`;
  } else {
    // If we need to wrap the word around two lines
    const rectEnd = range.getClientRects()[1];
    const ttsPosTopEnd = Math.floor(scrollableRootTop + rectEnd.top);
    const ttsWidthEnd = rectEnd.width;
    const ttsLeftEnd = rectEnd.left;
    const ttsHeightEnd = rectEnd.height;
    ttsIndicatorElements.start.style.top = `${ttsPosTopEnd}px`;
    ttsIndicatorElements.start.style.height = `${ttsHeightEnd}px`;
    ttsIndicatorElements.start.style.width = `${ttsWidthEnd}px`;
    ttsIndicatorElements.start.style.transform = `translateX(${ttsLeftEnd}px)`;
    ttsIndicatorElements.end.style.top = `${ttsPosTop}px`;
    ttsIndicatorElements.end.style.height = `${ttsHeight}px`;
    ttsIndicatorElements.end.style.width = `${ttsWidth}px`;
    ttsIndicatorElements.end.style.opacity = '1';
    ttsIndicatorElements.end.style.transform = `translateX(${ttsTransformLeft}px)`;
  }

  setTimeout(() => {
    ttsIndicatorElements.start.classList.add('tts-position-indicator--with-transition');
    ttsIndicatorElements.end.classList.add('tts-position-indicator--with-transition');
  }, 0);

  if (contentContainer) {
    sendTTSIndicatorMovedEvent(scrollingManager, contentContainer, range, scrollableRoot);
  }

  return ttsPosTop;
}

export async function updateWordBoundaryIndicator({
  contentContainerParam,
  isAutoScrollEnabled,
  isPlaying = true,
  chunkId,
  position,
  scrollableRootId,
}: {
  contentContainerParam?: ChunkContainerElement;
  isAutoScrollEnabled: boolean;
  isPlaying?: boolean;
  chunkId: string;
  position: number;
  scrollableRootId?: string;
}) {
  const wordBoundariesForVoiceAndChunk = wordBoundariesForVoice?.[chunkId] ?? null;
  const nearestTtsPosition = findWordBoundaryForTrackPosition(position, wordBoundariesForVoiceAndChunk ?? []);

  // If the track position is over 2 seconds away from our progress, most likely this is not right
  // Lets be patient and not jump to that spot right away
  if (nearestTtsPosition && Math.abs(nearestTtsPosition.trackPos - position) < 2) {
    let scrollableRoot: HTMLElement;
    let contentContainer: ChunkContainerElement | undefined;
    if (isInReactNativeWebView) {
      const mobileScrollingManager = getScrollingManager('updateWordBoundaryIndicator');
      // The manager-local flag goes false while a paginated gesture holds auto-scroll
      // suspended — don't switch chunks under the user's finger.
      if (isAutoScrollEnabled && mobileScrollingManager.ttsAutoScrollingEnabled) {
        const chunkContainer = getChunkContainerByChunkId(chunkId);
        if (!chunkContainer) {
          throw new TextToSpeechContentFrameError(`No chunk for ${chunkId} found`);
        }
        await mobileScrollingManager.setNewChunkIndex(Number(chunkContainer.dataset.chunkIndex), 10);
      }
      scrollableRoot = mobileScrollingManager.getScrollingElement();
      contentContainer = mobileScrollingManager.getCurrentChunkContainer();
    } else {
      if (!scrollableRootId) {
        throw new Error('!scrollableRootId');
      }
      const scrollableRootElement = document.getElementById(scrollableRootId);
      if (!scrollableRootElement) {
        throw new Error('!scrollableRootElement');
      }
      scrollableRoot = scrollableRootElement;
      contentContainer = contentContainerParam;
    }

    let skipIndicatorUpdate = false;
    if (isInReactNativeWebView) {
      const mobileScrollingManager = getScrollingManager('updateWordBoundaryIndicator');
      skipIndicatorUpdate = mobileScrollingManager.getCurrentChunkId() !== chunkId;
    }

    scrollToTtsPosition({
      contentContainer,
      isAutoScrollEnabled,
      scrollableRoot,
      ttsPosition: nearestTtsPosition,
      ttsChunkId: chunkId,
      isTtsActivelyPlaying: isPlaying,
      skipIndicatorUpdate,
    });
  } else {
    portalGateToForeground.emit('fetch-word-boundaries');
  }
}
export async function createHighlightAtTtsPosition(contentContainer: HTMLElement, ttsPos: TtsPosition) {
  const { paraIndex } = ttsPos;

  populateOrRepopulateTTSAbleElements(contentContainer);
  const node = ttsAbleElements[paraIndex];

  if (!node.textContent) {
    throw new TextToSpeechContentFrameError('Could not find text node');
  }

  const highlightRange = new Range();
  highlightRange.selectNode(node);

  const selection = document.getSelection();
  if (!selection) {
    throw new TextToSpeechContentFrameError('no selection');
  }
  selection.removeAllRanges();
  selection.addRange(highlightRange);

  portalGateToForeground.emit('create-highlight');
}


window.tts = {
  getPositionForTtsFromCurrentScrollPosition,
  getFocusableElementIndexFromCurrentScrollPosition,
  findTTSableElementFromCenterOfViewport,
  updateWordBoundaryIndicator,
  getRectsFromTtsPosition,
  wordBoundariesForVoice,
  ttsAbleElements,
};
